import React, { useEffect, useState } from 'react';
import {
  Inbox, Loader2, Search, Filter, Plus, MessageCircle, Clock, CheckCircle,
  AlertTriangle, X, Send, ArrowLeft, User, Calendar, Tag,
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { supabase } from '../lib/supabase';

interface Ticket {
  id: string;
  user_id: string | null;
  user_email: string;
  admin_email: string | null;
  subject: string;
  message: string;
  response: string | null;
  status: 'open' | 'in_progress' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  category: string;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  metadata?: {
    source?: string;
    ticket_id?: string;
    screenshot_url?: string;
    [key: string]: any;
  };
}

const STATUS_CONFIG = {
  open: { label: 'Offen', badge: 'badge-warning', icon: <Clock size={12} /> },
  in_progress: { label: 'In Bearbeitung', badge: 'badge-info', icon: <MessageCircle size={12} /> },
  closed: { label: 'Geschlossen', badge: 'badge-success', icon: <CheckCircle size={12} /> },
};

const PRIORITY_CONFIG = {
  low: { label: 'Niedrig', color: 'var(--text-muted)' },
  normal: { label: 'Normal', color: 'var(--info)' },
  high: { label: 'Hoch', color: 'var(--warning)' },
  urgent: { label: 'Dringend', color: 'var(--danger)' },
};

const CATEGORY_LABELS: Record<string, string> = {
  general: 'Allgemein',
  technical: 'Technisch',
  billing: 'Abrechnung',
  content: 'Inhalt',
  feature_request: 'Feature-Wunsch',
  bug: 'Bug-Report',
  question_report: 'Fragen-Meldung',
};

export const SupportPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [responseText, setResponseText] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTicket, setNewTicket] = useState({ user_email: '', subject: '', message: '', priority: 'normal', category: 'general' });

  useEffect(() => { loadTickets(); }, []);

  const loadTickets = async () => {
    setLoading(true);
    const { data, error } = await adminDb.from('support_tickets').select('*').order('created_at', { ascending: false });
    if (!error) setTickets(data || []);
    setLoading(false);
  };

  const filtered = React.useMemo(() => {
    let result = [...tickets];
    if (filterStatus) result = result.filter(t => t.status === filterStatus);
    if (filterPriority) result = result.filter(t => t.priority === filterPriority);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t =>
        t.subject.toLowerCase().includes(term) ||
        t.user_email.toLowerCase().includes(term) ||
        t.message.toLowerCase().includes(term)
      );
    }
    return result;
  }, [tickets, filterStatus, filterPriority, searchTerm]);

  const handleRespond = async () => {
    if (!selectedTicket || !responseText.trim()) return;
    setSaving(true);
    const session = await supabase.auth.getSession();
    const adminEmail = session.data.session?.user?.email || 'unknown';

    await adminDb.from('support_tickets').update({
      response: responseText,
      status: 'in_progress',
      admin_email: adminEmail,
      updated_at: new Date().toISOString(),
    }).eq('id', selectedTicket.id);

    await adminDb.from('audit_logs').insert({
      admin_email: adminEmail,
      action: 'support.respond',
      resource_type: 'support_ticket',
      resource_id: selectedTicket.id,
      new_value: { response: responseText.substring(0, 100) },
    });

    setSaving(false);
    setResponseText('');
    setSelectedTicket(null);
    await loadTickets();
  };

  const handleCloseTicket = async (ticket: Ticket) => {
    const session = await supabase.auth.getSession();
    const adminEmail = session.data.session?.user?.email || 'unknown';

    await adminDb.from('support_tickets').update({
      status: 'closed',
      closed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      admin_email: adminEmail,
    }).eq('id', ticket.id);

    await adminDb.from('audit_logs').insert({
      admin_email: adminEmail,
      action: 'support.close',
      resource_type: 'support_ticket',
      resource_id: ticket.id,
    });

    setSelectedTicket(null);
    await loadTickets();
  };

  const handleCreateTicket = async () => {
    if (!newTicket.user_email || !newTicket.subject || !newTicket.message) return;
    setSaving(true);
    await adminDb.from('support_tickets').insert({
      user_email: newTicket.user_email,
      subject: newTicket.subject,
      message: newTicket.message,
      priority: newTicket.priority,
      category: newTicket.category,
      status: 'open',
    });
    setSaving(false);
    setShowCreate(false);
    setNewTicket({ user_email: '', subject: '', message: '', priority: 'normal', category: 'general' });
    await loadTickets();
  };

  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
  const closedToday = tickets.filter(t => t.status === 'closed' && t.closed_at && new Date(t.closed_at).toDateString() === new Date().toDateString()).length;

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><Inbox size={24} /> Support</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  // Detail View
  if (selectedTicket) {
    const sc = STATUS_CONFIG[selectedTicket.status];
    const pc = PRIORITY_CONFIG[selectedTicket.priority];
    return (
      <div>
        <div className="page-header">
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedTicket(null)}>
            <ArrowLeft size={14} /> Zurück
          </button>
          <div className="flex gap-2">
            {selectedTicket.status !== 'closed' && (
              <button className="btn btn-secondary btn-sm" onClick={() => handleCloseTicket(selectedTicket)}>
                <CheckCircle size={14} /> Schliessen
              </button>
            )}
          </div>
        </div>

        <div className="card mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2>{selectedTicket.subject}</h2>
            <div className="flex gap-2">
              <span className={`badge ${sc.badge}`}>{sc.icon} {sc.label}</span>
              <span className="badge" style={{ borderColor: pc.color, color: pc.color }}>{pc.label}</span>
              <span className="badge badge-neutral">{CATEGORY_LABELS[selectedTicket.category]}</span>
            </div>
          </div>
          <div className="flex gap-4 text-xs text-muted mb-4">
            <span className="flex items-center gap-1"><User size={12} /> {selectedTicket.user_email}</span>
            <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(selectedTicket.created_at).toLocaleString('de-DE')}</span>
            {selectedTicket.admin_email && (
              <span className="flex items-center gap-1"><Tag size={12} /> Bearbeiter: {selectedTicket.admin_email}</span>
            )}
          </div>

          <div style={{ background: 'var(--bg-elevated)', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <p className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>Nachricht des Nutzers:</p>
            <p style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.message}</p>
          </div>

          {/* Screenshot attachment */}
          {selectedTicket.metadata?.screenshot_url && (
            <div style={{ marginBottom: 16 }}>
              <p className="text-sm" style={{ fontWeight: 600, marginBottom: 8 }}>📎 Anhang:</p>
              <a href={selectedTicket.metadata.screenshot_url} target="_blank" rel="noopener noreferrer">
                <img
                  src={selectedTicket.metadata.screenshot_url}
                  alt="Screenshot"
                  style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }}
                />
              </a>
            </div>
          )}

          {/* Ticket source badge */}
          {selectedTicket.metadata?.source === 'in_app_support' && (
            <div className="flex items-center gap-2 mb-4">
              <span className="badge badge-info">📱 In-App Support</span>
              {selectedTicket.metadata?.ticket_id && (
                <span className="badge badge-neutral">{selectedTicket.metadata.ticket_id}</span>
              )}
            </div>
          )}

          {selectedTicket.response && (
            <div style={{ background: 'var(--success-bg, #e8f5e9)', padding: 16, borderRadius: 8, marginBottom: 16, borderLeft: '3px solid var(--success)' }}>
              <p className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>Admin-Antwort:</p>
              <p style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.response}</p>
            </div>
          )}

          {selectedTicket.status !== 'closed' && (
            <div>
              <p className="text-sm" style={{ fontWeight: 600, marginBottom: 8 }}>Antworten:</p>
              <textarea
                className="input"
                rows={4}
                placeholder="Antwort verfassen..."
                value={responseText}
                onChange={e => setResponseText(e.target.value)}
              />
              <div className="flex justify-end mt-3">
                <button className="btn btn-primary" onClick={handleRespond} disabled={saving || !responseText.trim()}>
                  {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
                  Antwort senden
                </button>
              </div>
            </div>
          )}
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1><Inbox size={24} /> Support</h1>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> Ticket erstellen
        </button>
      </div>

      {/* KPIs */}
      <div className="grid-cols-4 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Gesamt</span>
          <span className="kpi-value">{tickets.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Offen</span>
          <span className="kpi-value" style={{ color: openCount > 0 ? 'var(--warning)' : 'var(--success)' }}>{openCount}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">In Bearbeitung</span>
          <span className="kpi-value" style={{ color: 'var(--info)' }}>{inProgressCount}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Heute geschlossen</span>
          <span className="kpi-value" style={{ color: 'var(--success)' }}>{closedToday}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="table-container">
        <div className="table-toolbar">
          <div className="flex gap-3 items-center" style={{ flex: 1, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', minWidth: 200 }}>
              <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input className="input input-search" placeholder="Suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <select className="input" style={{ width: 150 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Alle Status</option>
              <option value="open">Offen</option>
              <option value="in_progress">In Bearbeitung</option>
              <option value="closed">Geschlossen</option>
            </select>
            <select className="input" style={{ width: 130 }} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
              <option value="">Alle Prioritaeten</option>
              <option value="urgent">Dringend</option>
              <option value="high">Hoch</option>
              <option value="normal">Normal</option>
              <option value="low">Niedrig</option>
            </select>
          </div>
          <span className="text-xs text-muted">{filtered.length} Tickets</span>
        </div>

        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Betreff</th>
              <th>Nutzer</th>
              <th>Kategorie</th>
              <th>Prioritaet</th>
              <th>Erstellt</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                {tickets.length === 0 ? 'Noch keine Support-Tickets.' : 'Keine Treffer.'}
              </td></tr>
            ) : (
              filtered.map(t => {
                const sc = STATUS_CONFIG[t.status];
                const pc = PRIORITY_CONFIG[t.priority];
                return (
                  <tr key={t.id} onClick={() => setSelectedTicket(t)} style={{ cursor: 'pointer' }}>
                    <td><span className={`badge ${sc.badge}`}>{sc.icon} {sc.label}</span></td>
                    <td style={{ fontWeight: 600 }}>{t.subject}</td>
                    <td className="text-sm">{t.user_email}</td>
                    <td><span className="badge badge-neutral">{CATEGORY_LABELS[t.category] || t.category}</span></td>
                    <td><span style={{ color: pc.color, fontWeight: 600, fontSize: 13 }}>{pc.label}</span></td>
                    <td className="text-xs text-muted">{new Date(t.created_at).toLocaleDateString('de-DE')}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 520, maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
            <div className="flex items-center justify-between mb-4">
              <h3>Neues Ticket</h3>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Nutzer E-Mail *</label>
                <input className="input" value={newTicket.user_email} onChange={e => setNewTicket(p => ({ ...p, user_email: e.target.value }))} placeholder="nutzer@example.de" />
              </div>
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Betreff *</label>
                <input className="input" value={newTicket.subject} onChange={e => setNewTicket(p => ({ ...p, subject: e.target.value }))} placeholder="Problembeschreibung" />
              </div>
              <div className="grid-cols-2" style={{ gap: 12 }}>
                <div>
                  <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Prioritaet</label>
                  <select className="input" value={newTicket.priority} onChange={e => setNewTicket(p => ({ ...p, priority: e.target.value }))}>
                    <option value="low">Niedrig</option>
                    <option value="normal">Normal</option>
                    <option value="high">Hoch</option>
                    <option value="urgent">Dringend</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Kategorie</label>
                  <select className="input" value={newTicket.category} onChange={e => setNewTicket(p => ({ ...p, category: e.target.value }))}>
                    <option value="general">Allgemein</option>
                    <option value="technical">Technisch</option>
                    <option value="billing">Abrechnung</option>
                    <option value="content">Inhalt</option>
                    <option value="feature_request">Feature-Wunsch</option>
                    <option value="bug">Bug-Report</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Nachricht *</label>
                <textarea className="input" rows={4} value={newTicket.message} onChange={e => setNewTicket(p => ({ ...p, message: e.target.value }))} placeholder="Beschreibung des Problems..." />
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Abbrechen</button>
                <button className="btn btn-primary" onClick={handleCreateTicket} disabled={saving || !newTicket.user_email || !newTicket.subject || !newTicket.message}>
                  {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={14} />}
                  Erstellen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
